import React from 'react';
import ReportingClassesReportPage from './ReportingClassesReportPage';
import {
  SORT_TYPES, CLASSES_REGISTRATIONS_COLUMNS, getDefaultVisibleColumns, getDefaultHiddenColumns,
} from '../constants/classesRegistrationsColumnsConfig';
import { buildClassesRegistrationsFilterGroups, CLASSES_REGISTRATIONS_FILTER_IDS, pickFilterParams } from '../constants/classesFiltersConfig';
import { compactCurrency, formatVenueDateTime } from '../utils/reportingUtils';

const CLASSES_REGISTRATIONS_CONFIG = {
  pageTitle: 'Class Registrations',
  pageSubtitle: 'One row per attendee on a paid or comped registration. Canceled registrations are listed but not counted as active registrations, comps or attendance; Refunded / Disputed and Net cover every listed row.',
  sidebarKey: 'classes_registrations',
  defaultPreset: 'year_to_date',
  dateModeOptions: [
    { key: 'order_date', label: 'Purchase Date' },
    { key: 'event_date', label: 'Class Date' },
  ],
  urlFilterIds: CLASSES_REGISTRATIONS_FILTER_IDS,

  sortTypes: SORT_TYPES,
  localStorageKey: (teamId) => `reportingClassesRegistrationsColumns_${teamId}`,
  firstColumnId: 'attendee',
  defaultSortRules: [{ field: 'registered_at', direction: 'desc' }],

  kpiFromDetailResponse: true,
  salesDataKey: null,
  venueSeriesKey: null,
  exportBasePath: (teamId) => `/teams/${teamId}/reporting/classes/registrations/export`,
  detailEndpoint: (teamId) => `/teams/${teamId}/reporting/classes/registrations/data.json`,
  detailResponseKey: 'registrations',
  detailErrorMessage: 'Failed to load class registrations.',
  detailTableTitle: 'Registrations',
  emptyMessage: 'No registrations in this range.',
  searchPlaceholder: 'Search attendees, emails, classes...',
  paginationLabel: 'registrations',
  itemLabel: (count) => `registration${count !== 1 ? 's' : ''}`,

  buildKpiCards: (current, prior) => [
    { label: 'Active Paid Registrations', value: (current.registrations || 0).toLocaleString(), current: current.registrations || 0, prior: (prior && prior.registrations) || 0 },
    { label: 'Active Comps', value: (current.comps || 0).toLocaleString(), current: current.comps || 0, prior: (prior && prior.comps) || 0 },
    { label: 'Refunded / Disputed', value: (current.refunded || 0).toLocaleString(), current: current.refunded || 0, prior: (prior && prior.refunded) || 0 },
    { label: 'Net', value: compactCurrency(current.net || 0), current: current.net || 0, prior: (prior && prior.net) || 0 },
  ],
  kpiGridColumns: 4,

  overTimeKey: null,
  topCharts: [],
  groupByOptions: [],
  groupByDefault: null,

  renderCellValue: (col, row, { currencyFormatter: cf, team }) => {
    switch (col.id) {
      case 'attendee': return row.attendee || '—';
      case 'email': return row.email || '—';
      case 'class_name': return team?.id ? <a href={`/teams/${team.id}/classes/${row.class_instance_id}`}>{row.class_name}</a> : row.class_name;
      case 'venue_name': return row.venue_name || '—';
      case 'first_session_at': return <span className="text-nowrap">{formatVenueDateTime(row.first_session_at, row.time_zone)}</span>;
      case 'status': return row.status_label || '—';
      case 'registration_type': return row.registration_type_label || '—';
      case 'order_id': return team?.id ? <a href={`/teams/${team.id}/orders/${row.order_id}`}>{`#${row.order_id}`}</a> : `#${row.order_id}`;
      case 'registered_at': return <span className="text-nowrap">{formatVenueDateTime(row.registered_at, row.time_zone)}</span>;
      case 'amount': return cf.format(row.amount || 0);
      case 'net': return cf.format(row.net || 0);
      case 'payment_status': return row.payment_status_label || '—';
      case 'sessions_attended': return row.counted_sessions == null ? '—' : `${row.sessions_attended} of ${row.counted_sessions}`;
      default: return '—';
    }
  },

  filterTitle: 'Filter Registrations',
  filterSubtitle: 'Apply filters to narrow class registrations.',
};

const ReportingClassesRegistrationsPage = (props) => (
  <ReportingClassesReportPage
    {...props}
    config={{
      ...CLASSES_REGISTRATIONS_CONFIG,
      columns: CLASSES_REGISTRATIONS_COLUMNS,
      getDefaultVisibleColumns,
      getDefaultHiddenColumns,
      buildDetailApiFilters: pickFilterParams(CLASSES_REGISTRATIONS_FILTER_IDS),
    }}
    reportPath="registrations"
    filterErrorMessage="Could not load the registration filters. Reload the page to try again."
    buildFilterGroups={buildClassesRegistrationsFilterGroups}
  />
);

export default ReportingClassesRegistrationsPage;
